'use client';

import { useState, useEffect } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { Product } from '@/types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import Image from 'next/image';

interface SalesClientProps {
  initialProducts: Product[];
  categories: string[];
  pagination: {
    currentPage: number;
    totalPages: number;
    totalCount: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
  locale: string;
}

export function SalesClient({ 
  initialProducts, 
  categories, 
  pagination, 
  locale 
}: SalesClientProps) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const t = useTranslations('Sales');
  
  const [products, setProducts] = useState(initialProducts);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || '');
  const [currentPage, setCurrentPage] = useState(pagination.currentPage);

  const updateURL = (params: Record<string, string>) => {
    const newSearchParams = new URLSearchParams(searchParams);
    
    Object.entries(params).forEach(([key, value]) => {
      if (value) {
        newSearchParams.set(key, value);
      } else {
        newSearchParams.delete(key);
      }
    });

    router.push(`/${locale}/sales?${newSearchParams.toString()}`);
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    updateURL({ search, category: selectedCategory, page: '1' });
  };

  const handleCategoryChange = (category: string) => {
    setSelectedCategory(category);
    updateURL({ search, category, page: '1' });
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    updateURL({ search, category: selectedCategory, page: page.toString() });
  };

  const calculateDiscount = (price: number, oldPrice: number) => {
    return Math.round(((oldPrice - price) / oldPrice) * 100);
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('kk-KZ', {
      style: 'currency',
      currency: 'KZT'
    }).format(price);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-primary to-primary/80 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center">
            <h1 className="text-4xl font-bold mb-4">
              {t('heroTitle')}
            </h1>
            <p className="text-xl mb-6">
              {t('subtitle')}
            </p>
            <div className="inline-flex items-center bg-white/20 rounded-lg px-6 py-3">
              <span className="text-lg font-semibold">
                {t('productsOnSale', { count: pagination.totalCount })}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Card className="p-6 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-2">
              <form onSubmit={handleSearch} className="flex space-x-2">
                <Input
                  placeholder={t('searchPlaceholder')}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="flex-1"
                />
                <Button type="submit">{t('search')}</Button>
              </form>
            </div>
            <div>
              <select
                className="w-full p-2 border border-gray-300 rounded-md"
                value={selectedCategory}
                onChange={(e) => handleCategoryChange(e.target.value)}
              >
                <option value="">{t('allCategories')}</option>
                {categories.map((category) => (
                  <option key={category} value={category}>
                    {category}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </Card>

        {/* Products Grid */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">{t('loadingProducts')}</p>
          </div>
        ) : products.length === 0 ? (
          <div className="text-center py-12">
            <h3 className="text-2xl font-semibold text-gray-900 mb-4">
              {t('noProductsFound')}
            </h3>
            <p className="text-gray-600 mb-6">
              {search || selectedCategory 
                ? t('noProductsMessage')
                : t('checkBackSoon')
              }
            </p>
            <Button onClick={() => {
              setSearch('');
              setSelectedCategory('');
              updateURL({ search: '', category: '', page: '1' });
            }}>
              {t('clearFilters')}
            </Button>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-8">
              {products.map((product) => (
                <Card key={product.sku} className="overflow-hidden hover:shadow-lg transition-shadow">
                  <div className="relative">
                    {/* Discount Badge */}
                    {product.old_price && product.price && (
                      <div className="absolute top-4 left-4 z-10">
                        <Badge className="bg-green-600 text-white font-bold px-3 py-1">
                          {t('discount', { percent: calculateDiscount(product.price, product.old_price) })}
                        </Badge>
                      </div>
                    )}
                    
                    {/* Product Image Placeholder */}
                    <div className="h-48 p-4 flex items-center justify-center">
                      <Image
                        src={product.medias_0 || "/placeholder.svg"}
                        alt={"image"}
                        width={150}
                        height={150}
                        className="object-contain"
                      />
                    </div>
                  </div>
                  
                  <div className="p-4">
                    <div className="mb-2">
                      <Badge variant="secondary" className="text-xs">
                        {product.category}
                      </Badge>
                    </div>
                    
                    <h3 className="font-semibold text-lg mb-2 line-clamp-2">
                      {product.model_name}
                    </h3>
                    
                    <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                      {product.long_description || t('professionalEquipment')}
                    </p>
                    
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center space-x-2">
                        <span className="text-2xl font-bold text-red-600">
                          {product.price ? formatPrice(product.price) : 'N/A'}
                        </span>
                        {product.old_price && (
                          <span className="text-lg text-gray-500 line-through">
                            {formatPrice(product.old_price)}
                          </span>
                        )}
                      </div>
                    </div>
                    
                    {product.old_price && product.price && (
                      <div className="mb-4 p-2 bg-green-50 rounded">
                        <p className="text-sm text-green-700 font-medium">
                          {t('youSave', { amount: formatPrice(product.old_price - product.price) })}
                        </p>
                      </div>
                    )}
                    
                    <div className="space-y-2">
                      <Button 
                        className="w-full bg-green-600 hover:bg-red-700"
                        onClick={() => router.push(`/${locale}/product/${product.sku}`)}
                      >
                        {t('viewDetails')}
                      </Button>
                      {/* <Button 
                        variant="outline" 
                        className="w-full"
                        onClick={() => {
                          // Add to cart functionality would go here
                          alert('Added to cart!');
                        }}
                      >
                        {t('addToCart')}
                      </Button> */}
                    </div>
                    
                    {product.sku && (
                      <p className="text-xs text-gray-500 mt-2">
                        {t('sku', { sku: product.sku })}
                      </p>
                    )}
                  </div>
                </Card>
              ))}
            </div>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div className="flex justify-center items-center space-x-4">
                <Button
                  variant="outline"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={!pagination.hasPrev}
                >
                  {t('previous')}
                </Button>
                
                <div className="flex items-center space-x-2">
                  {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                    const pageNum = Math.max(1, Math.min(
                      pagination.totalPages - 4,
                      currentPage - 2
                    )) + i;
                    
                    if (pageNum > pagination.totalPages) return null;
                    
                    return (
                      <Button
                        key={pageNum}
                        variant={pageNum === currentPage ? "default" : "outline"}
                        onClick={() => handlePageChange(pageNum)}
                        className="w-10 h-10 p-0"
                      >
                        {pageNum}
                      </Button>
                    );
                  })}
                </div>
                
                <Button
                  variant="outline"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={!pagination.hasNext}
                >
                  {t('next')}
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
